// zod schemas for the plumber2 API responses — the single source of truth for the
// network boundary. Each schema yields BOTH a runtime validator (catches the
// API drifting from what the UI expects, at the seam where it originates) and a
// static type (via z.infer). Shapes verified against test/fixtures/*.json and,
// when the data is on disk, the data product itself (schemas-data.test.ts).
//
// Object schemas are .passthrough(): a successful zod parse drops every key the
// schema does not name, and the UI reads fields no schema lists (percent_levels,
// units, updated). Validation only warns (api.ts), so a strict schema would not
// catch drift anyway; it would silently delete data.
import { z } from "zod";

/** A localized string map, e.g. { en: "GDP" }. `label()` picks `.en` or the first. */
export const Translated = z.record(z.string(), z.string());
export type Translated = z.infer<typeof Translated>;

/** Catalog `source` is a plain string (schema 1.0) or an object (1.1+);
 * meta `source` is this object. `key` arrives with the shared sources.json. */
export const SourceObject = z.object({
  name: Translated,
  url: z.union([z.string(), Translated]).optional(),
  key: z.string().optional(),
});
export type SourceObject = z.infer<typeof SourceObject>;

// Several fields are a string when meaningful and an empty object {} from the R
// serializer when "absent": `featured` (chip label), `topic`, and `updated`
// (datetime string). This `string | {}` sentinel is the single most common shape
// quirk in the API — exactly the class the original JS hand-patched against.
const stringOrEmpty = z.union([z.string(), z.record(z.string(), z.unknown())]);
const featured = stringOrEmpty.optional();
/** A keyed label, { key, name } (schema 1.1 topic, and map source/topic). */
const Keyed = z.object({ key: z.string(), name: Translated });
/** `concept`: a string (1.0), or the full path as a label object whose `leaf`
 * is the last segment, itself a label object ({ en: "A / B", leaf: { en: "B" } }). */
const Concept = z.union([
  z.string(),
  z.object({ leaf: z.union([z.string(), Translated]).optional() }).catchall(z.string()),
]);
const maybeStampOrEmpty = stringOrEmpty.optional();

/** One row of GET /catalog. */
export const CatalogEntry = z.object({
  id: z.string(),
  title: Translated,
  concept: Concept,
  canonical: z.boolean(),
  featured,
  topic: stringOrEmpty,
  source: z.union([z.string(), SourceObject]),
  license: z.string(),
  frequency: z.string(),
  start: z.string(),
  end: z.string(),
  n_series: z.number(),
  updated: maybeStampOrEmpty,
  fetched: z.string().optional(),
  load: z.string().optional(),
  data: z.string().optional(),
  meta: z.string().optional(),
}).passthrough();
export type CatalogEntry = z.infer<typeof CatalogEntry>;

export const Catalog = z.array(CatalogEntry);
export type Catalog = z.infer<typeof Catalog>;

/** One row of GET /maps: a map of maps.dataseries.org. `series` names the
 * datasets here that show the same statistic (empty = a map-only entry).
 * `concept` (the subhead in the topic list) and `thumbnail` came with
 * maps.json 1.1; older lists lack them. Named here because this schema is not
 * passthrough: an unnamed key would be dropped. */
export const MapEntry = z.object({
  id: z.string(),
  url: z.string(),
  title: Translated,
  source: Keyed,
  topic: Keyed,
  frequency: z.string(),
  levels: z.array(z.object({ key: z.string(), frequency: z.string(), label: Translated })),
  levels_label: Translated,
  start: z.string(),
  end: z.string(),
  series: z.array(z.string()),
  concept: Translated.optional(),
  thumbnail: z.string().optional(),
});
export type MapEntry = z.infer<typeof MapEntry>;

export const Maps = z.array(MapEntry);
export type Maps = z.infer<typeof Maps>;

/** One row of GET /categories — the ordered concept-group overview grouping.
 * name/blurb are plain strings (schema 1.0) or label objects (1.1+). */
export const Category = z.object({
  // `key` is the STABLE identifier the catalog's topic.key joins on. It must be
  // declared: zod strips unknown keys, so omitting it silently deleted the field
  // and catName() fell back to matching on the display name, which never equals
  // the key. The browse view then rendered raw slugs ("national-accounts") as
  // group headings, in every language including English.
  key: z.string().optional(),
  name: z.union([z.string(), Translated]),
  blurb: z.union([z.string(), Translated]).optional(),
});
export type Category = z.infer<typeof Category>;

export const Categories = z.array(Category);
export type Categories = z.infer<typeof Categories>;

/** A selectable level within a dimension. */
export const Level = z.object({
  label: Translated,
  data: z.boolean(),
}).passthrough();
export type Level = z.infer<typeof Level>;

// hierarchy is a recursive tree: { code: [] | { childCode: [] | {...} } }.
type HierNode = unknown[] | { [code: string]: HierNode };
const HierNode: z.ZodType<HierNode> = z.lazy(() =>
  z.union([z.array(z.unknown()), z.record(z.string(), HierNode)]),
);

export const Dimension = z.object({
  label: Translated,
  levels: z.record(z.string(), Level),
  hierarchy: z.record(z.string(), HierNode).optional(),
}).passthrough();
export type Dimension = z.infer<typeof Dimension>;

/** GET /dataset/<id>/meta. */
export const DatasetMeta = z.object({
  schema_version: z.string(),
  id: z.string(),
  dim_order: z.array(z.string()),
  start: z.string(),
  end: z.string(),
  title: Translated,
  source: z.union([z.string(), SourceObject]),
  license: z.string(),
  frequency: z.string(),
  dimensions: z.record(z.string(), Dimension),
  topic: z.union([z.string(), Keyed, Translated]).optional(),
  concept: Concept.optional(),
  canonical: z.boolean().optional(),
  featured,
  split: z.string().nullable().optional(),
  // May be a single dim or several (the datasheet can list "a, b") — io.R emits a
  // scalar or an array accordingly, and singleSelectDims() normalises both.
  single_select: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  default_transform: z.string().optional(),
  // An empty default is serialized as [] (single-series datasets); read as {}.
  default: z.union([
    z.record(z.string(), z.string()),
    z.array(z.never()).max(0).transform((): Record<string, string> => ({})),
  ]).optional(),
  fetched_utc: z.string().optional(),
}).passthrough();
export type DatasetMeta = z.infer<typeof DatasetMeta>;

// GET /series?dataset=&dims=&from=&to= -> columnar JSON: one string[] per selected
// dimension, plus `date` (string[]) and `value` (number[]). The dim columns are
// the catchall keys.
export const SeriesResponse = z
  .object({
    date: z.array(z.string()),
    value: z.array(z.number().nullable()),
  })
  .catchall(z.array(z.string()));
export type SeriesResponse = z.infer<typeof SeriesResponse>;
